import {Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, HostBinding} from '@angular/core';
import {DICTIONARY} from '../../../dictionary';

// a value the reviewer can act on from an analysis row (e.g. sender email or its domain, a URL, a file hash)
export type SrRowActionTarget = {
    label: string;
    value: string;
    // free data passed back to the owner as is (item type, attachment object, etc.)
    data?: any;
    // CC: hides "All plans > Allow" when the backend cannot undo a global block for this value (domains, IPs)
    noGlobalAllow?: boolean;
};

export type SrRowActionEvent = {
    scope: 'global' | 'plan';
    block: boolean;
    target: SrRowActionTarget;
    done: () => void;
};

// security reviewer action on a single analysis row: pick the value (email / domain ...), then block or allow it globally or in the plan, then confirm
@Component({
    selector: 'sr-row-action',
    templateUrl: './sr-row-action.component.html',
    styleUrls: ['./sr-row-action.component.scss']
})
export class SrRowActionComponent implements OnDestroy {
    constructor(private elementRef: ElementRef) {}

    dic = DICTIONARY;

    @Input() targets: SrRowActionTarget[] = [];
    // plan scope is offered only when the owner can apply it
    @Input() allowPlan = true;
    @Output() apply = new EventEmitter<SrRowActionEvent>();
    // inside a status column cell the shield is centered (like the other status icons); inline it sits after the row icons with a gap
    @HostBinding('class.in-cell') @Input() cell = false;

    // only one row menu is open at a time
    private static openInstance: SrRowActionComponent;

    isOpen = false;
    inProcess = false;
    menuPos = {top: 0, left: 0};
    selectedTarget: SrRowActionTarget;
    pending: {scope: 'global' | 'plan', block: boolean};

    toggle(event) {
        event.stopPropagation();
        if (this.isOpen) {
            this.close();
            return;
        }
        // another row's action is still saving - keep that menu (and its result) in place
        if (SrRowActionComponent.openInstance?.inProcess) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const menuWidth = 280;
        const gap = 6;
        this.menuPos = {
            top: rect.bottom + gap,
            left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8))
        };
        SrRowActionComponent.openInstance?.close();
        SrRowActionComponent.openInstance = this;
        this.selectedTarget = this.targets[0];
        this.pending = null;
        this.isOpen = true;
        // CC: capture phase, only while open: scrolling any inner container (analysis cards) closes the fixed-position menu, without a listener per row on every scroll
        document.addEventListener('scroll', this.onAnyScroll, true);

        // after render: flip above the shield when the menu would leave the viewport
        setTimeout(() => {
            const menuEl = this.elementRef.nativeElement.querySelector('.sr-row-action-menu');
            if (!menuEl || rect.bottom + gap + menuEl.offsetHeight <= window.innerHeight - 8) {
                return;
            }
            this.menuPos = {top: Math.max(8, rect.top - gap - menuEl.offsetHeight), left: this.menuPos.left};
        });
    }

    confirm() {
        this.inProcess = true;
        this.apply.emit({
            scope: this.pending.scope,
            block: this.pending.block,
            target: this.selectedTarget,
            done: () => {
                this.inProcess = false;
                this.close();
            }
        });
    }

    close() {
        if (this.inProcess) {
            return;
        }
        this.isOpen = false;
        this.pending = null;
        document.removeEventListener('scroll', this.onAnyScroll, true);
        if (SrRowActionComponent.openInstance === this) {
            SrRowActionComponent.openInstance = null;
        }
    }

    onAnyScroll = () => {
        if (this.isOpen) {
            this.close();
        }
    };

    @HostListener('document:click', ['$event'])
    onDocumentClick(event) {
        if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
            this.close();
        }
    }

    @HostListener('window:resize')
    onViewportChange() {
        this.close();
    }

    ngOnDestroy() {
        document.removeEventListener('scroll', this.onAnyScroll, true);
        if (SrRowActionComponent.openInstance === this) {
            SrRowActionComponent.openInstance = null;
        }
    }
}
